import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabaseClient';
import { styled } from '@mui/material/styles';
import logo from '../assets/logo0.png';

const StyledBox = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: `url(${process.env.PUBLIC_URL}/backgroundlogin.png)`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundAttachment: 'fixed',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backgroundBlendMode: 'overlay',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '400px',
  margin: '0 auto',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
}));

const StyledForm = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hash, setHash] = useState(null);

  useEffect(() => {
    // Get the hash from the URL
    const hashFromUrl = window.location.hash;
    if (hashFromUrl) {
      const decodedHash = hashFromUrl.substring(1); // Remove the # symbol
      setHash(decodedHash);
    }

    // Check for access token in URL
    const accessToken = new URLSearchParams(window.location.search).get('access_token');
    if (accessToken) {
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: null
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // If we have a hash from recovery email
      if (hash) {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: hash,
          type: 'recovery'
        });

        if (verifyError) throw verifyError;
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      setMessage({
        type: 'success',
        text: 'Password has been successfully reset!'
      });

      // Clear the hash from URL
      window.location.hash = '';

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Error:', error);
      setMessage({
        type: 'error',
        text: error.message || 'An error occurred while resetting password.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledBox>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 1 }}>
        <Toolbar>
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{ height: 40, mr: 2 }}
          />
          <Typography variant="h6" color="primary" sx={{ flexGrow: 1 }}>
            Password Reset
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container component="main" maxWidth="xs" sx={{ flex: 1, my: 4 }}>
        <StyledPaper elevation={3}>
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Set New Password
          </Typography>

          {message.text && (
            <Alert severity={message.type} sx={{ mt: 2, width: '100%' }}>
              {message.text}
            </Alert>
          )}

          <StyledForm onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="New Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />

            <SubmitButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading || !password || !confirmPassword}
            >
              {loading ? <CircularProgress size={24} /> : 'Reset Password'}
            </SubmitButton>
          </StyledForm>
        </StyledPaper>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: 'white',
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} Your Company Name. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </StyledBox>
  );
};

export default ResetPassword; 